import { compose, IDENTITY, scaling, translation, type Matrix } from '../geometry/matrix';
import { segmentsBounds, transformSegments, type Segment } from '../geometry/path';
import { parsePathCached } from '../scene/parseCache';
import type { NailShape, NailShapeId } from '../model/types';
import presetData from './nail-shapes.json';

/**
 * Seven preset silhouettes (docs/05). Each is a closed path whose actual geometry is
 * normalized to the unit square at evaluation time, so padded source coordinates can
 * never make a physical nail narrower than the entered width.
 */
const presetList = presetData.shapes as ReadonlyArray<{
  id: NailShapeId;
  version: string;
  normalizedPathD: string;
}>;

export const NAIL_SHAPE_PRESETS: Readonly<Record<NailShapeId, NailShape>> = Object.fromEntries(
  presetList.map((s) => [
    s.id,
    { id: s.id, version: s.version, normalizedPathD: s.normalizedPathD },
  ]),
) as Record<NailShapeId, NailShape>;

export const NAIL_SHAPE_LABELS: Readonly<Record<NailShapeId, string>> = {
  round: 'Round',
  oval: 'Oval',
  almond: 'Almond',
  square: 'Square',
  squoval: 'Squoval',
  coffin: 'Coffin',
  stiletto: 'Stiletto',
};

/**
 * Editable starting points, clearly not biological measurements (docs/05). The
 * 14 × 22 mm medium size matches the default example artboard (docs/01).
 */
export const NAIL_SIZE_PRESETS = {
  short: { label: 'Short example', widthMm: 13, lengthMm: 16 },
  medium: { label: 'Medium example', widthMm: 14, lengthMm: 22 },
  long: { label: 'Long example', widthMm: 14, lengthMm: 30 },
} as const;

export function getShapePreset(id: NailShapeId): NailShape {
  return NAIL_SHAPE_PRESETS[id];
}

/** Matrix mapping the silhouette's actual geometric bounds onto [0,1]². */
export function unitNormalization(shape: NailShape): Matrix {
  const bounds = segmentsBounds(parsePathCached(shape.normalizedPathD));
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return IDENTITY;
  return compose(scaling(1 / bounds.width, 1 / bounds.height), translation(-bounds.x, -bounds.y));
}

/** Nail silhouette in the nail's own millimeter space: bounds exactly `[0,0,width,length]`. */
export function nailSilhouetteSegments(
  shape: NailShape,
  widthMm: number,
  lengthMm: number,
): Segment[] {
  const m = compose(scaling(widthMm, lengthMm), unitNormalization(shape));
  return transformSegments(parsePathCached(shape.normalizedPathD), m);
}
